import { Alert, Card, Input, Select, Space, Switch, Table, Typography, message } from 'antd';
import { BasePageComponent } from 'components';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useSeasonList } from 'hooks/useSeason';
import { useSubtitleVendorUsers } from 'hooks/useSubtitleVendorUsers';
import { VendorNotificationEmailManager } from '../components/VendorNotificationEmailManager';
import {
  useCreateVendorSeasonMapping,
  useDeleteVendorSeasonMapping,
  useVendorSeasonList,
} from 'hooks/useVendorSeasonList';
import { SeasonData } from 'type/seasonType';

const { Title, Text } = Typography;

const formatUserOption = (u: {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}) => {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  const label = name ? `${u.username} (${name})` : u.username;
  return { value: u.id, label, key: u.id };
};

export const SubtitleVendorListPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [pendingSeasonId, setPendingSeasonId] = useState<number | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const { data: vendorUsers, isFetching: usersLoading } = useSubtitleVendorUsers();
  const userOptions = useMemo(
    () => (vendorUsers ?? []).map(formatUserOption),
    [vendorUsers]
  );

  const seasonQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    if (search) params.set('search', search);
    return params;
  }, [page, pageSize, search]);

  const { data: seasonData, isFetching: seasonsLoading } = useSeasonList(seasonQueryParams);

  const { data: mappingData, isFetching: mappingsLoading } = useVendorSeasonList(
    selectedUserId ? { user_id: selectedUserId, page: 1, page_size: 100 } : undefined,
    { enabled: !!selectedUserId }
  );

  const createMutation = useCreateVendorSeasonMapping();
  const deleteMutation = useDeleteVendorSeasonMapping();

  const mappingBySeasonId = useMemo(() => {
    const map = new Map<number, number>();
    (mappingData?.results ?? []).forEach(m => map.set(m.season, m.id));
    return map;
  }, [mappingData]);

  const handleToggle = async (season: SeasonData, checked: boolean) => {
    if (!selectedUserId) return;
    setPendingSeasonId(season.id);
    try {
      if (checked) {
        await createMutation.mutateAsync({
          user_id: selectedUserId,
          season_id: season.id,
        });
        message.success(`"${season.title}" provided to vendor`);
      } else {
        const mappingId = mappingBySeasonId.get(season.id);
        if (!mappingId) {
          message.error('Mapping not found');
          return;
        }
        await deleteMutation.mutateAsync(mappingId);
        message.success(`"${season.title}" removed from vendor`);
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err?.response?.data?.detail ?? 'Failed to update');
    } finally {
      setPendingSeasonId(null);
    }
  };

  return (
    <BasePageComponent>
      <div style={{ padding: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Subtitle Vendor Provision
          </Title>
          <Input.Search
            placeholder="Search by season title"
            allowClear
            onSearch={v => {
              setSearch(v);
              setPage(1);
            }}
            style={{ width: 320 }}
          />
        </Space>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>Vendor user</Text>
            <Select
              showSearch
              optionFilterProp="label"
              loading={usersLoading}
              value={selectedUserId ?? undefined}
              onChange={v => setSelectedUserId(typeof v === 'number' ? v : null)}
              placeholder="Select a subtitle-vendor user"
              style={{ width: 480 }}
              options={userOptions}
              allowClear
              onClear={() => setSelectedUserId(null)}
              notFoundContent={usersLoading ? 'Loading…' : 'No subtitle-vendor users'}
            />
          </Space>
        </Card>

        {!selectedUserId && (
          <Alert
            type="info"
            showIcon
            message="Select a vendor user above to manage their notification emails and season mappings."
            style={{ marginBottom: 16 }}
          />
        )}

        {selectedUserId && <VendorNotificationEmailManager userId={selectedUserId} />}

        <Table<SeasonData>
          rowKey="id"
          loading={seasonsLoading || (!!selectedUserId && mappingsLoading)}
          dataSource={seasonData?.results ?? []}
          pagination={{
            current: page,
            pageSize,
            total: seasonData?.count ?? 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 80 },
            {
              title: 'Title',
              dataIndex: 'title',
              ellipsis: true,
              render: (v: string, row: SeasonData) => v ?? row.slug,
            },
            {
              title: 'Created',
              dataIndex: 'created',
              width: 180,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
            {
              title: 'Provide to Vendor',
              width: 160,
              align: 'center',
              render: (_, row: SeasonData) => {
                const checked = mappingBySeasonId.has(row.id);
                const isPending = pendingSeasonId === row.id;
                return (
                  <Switch
                    checked={checked}
                    loading={isPending}
                    disabled={!selectedUserId}
                    onChange={c => handleToggle(row, c)}
                  />
                );
              },
            },
          ]}
        />
      </div>
    </BasePageComponent>
  );
};
